// client/src/contexts/NetworkProvider.tsx
//
// Rileva lo stato della rete (online/offline) su Web e Capacitor.
// Su mobile usa @capacitor/network, su web usa navigator.onLine.
//
// Espone:
//   useNetwork() → { isOnline: boolean }
//
// Integrazione in App.tsx: avvolgere <NetworkProvider> attorno a <Router>

import React, { createContext, useContext, useEffect, useState } from 'react';
import OfflineQueueBanner from '@/components/OfflineQueueBanner';

interface NetworkContextValue {
  isOnline: boolean;
}

const NetworkContext = createContext<NetworkContextValue>({ isOnline: true });

export function NetworkProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    // Prova a usare @capacitor/network (mobile)
    const initCapacitorNetwork = async () => {
      try {
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        setIsOnline(status.connected);

        const handler = await Network.addListener('networkStatusChange', (s) => {
          setIsOnline(s.connected);
        });
        cleanup = () => handler.remove();
      } catch {
        // Capacitor non disponibile — usa navigator.onLine (web)
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        cleanup = () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    };

    initCapacitorNetwork();

    return () => { cleanup?.(); };
  }, []);

  return (
    <NetworkContext.Provider value={{ isOnline }}>
      {children}
      <OfflineQueueBanner isOnline={isOnline} />
    </NetworkContext.Provider>
  );
}

export function useNetwork(): NetworkContextValue {
  return useContext(NetworkContext);
}
